import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Funnel, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { ContentCard, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { InventoryTabs } from "../components/InventoryTabs";
import { StockPositionFiltersModal } from "../components/StockPositionFiltersModal";
import { StockPositionTable } from "../components/StockPositionTable";
import { StockJournalFiltersModal } from "../components/StockJournalFiltersModal";
import { InventoryJournalTable } from "../components/InventoryJournalTable";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import { collectPageRecords } from "../../../lib/pagination";
import {
  aggregateStockPositionsByProduct,
  applyStockPositionOtherFilter,
  buildSystemSummariesFromRows,
  normalizeStockPositionOtherFilter,
  STOCK_POSITION_OTHER_FILTER,
} from "../utils/aggregateStockPositions";
import { filterJournalByRegisterType } from "../utils/registerTypeFilter";

const POSITION_FETCH_PAGE_SIZE = 50;
const FILTER_LOOKUP_PAGE_SIZE = 50;
const JOURNAL_REGISTER_FETCH_SIZE = 50;

function toFilterOptions(rows) {
  return (rows || []).map((row) => ({
    value: String(row.id),
    label: row.name || String(row.id),
  }));
}

function mergeFilterOptions(current, incoming) {
  const seen = new Set(current.map((option) => option.value));
  const merged = [...current];
  incoming.forEach((option) => {
    if (seen.has(option.value)) return;
    seen.add(option.value);
    merged.push(option);
  });
  return merged;
}

// Helper to format currency
const formatCurrency = (val) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
};

export function InventoryManagementPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "journal" ? "journal" : "position";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Position tab dataset states
  const [filteredData, setFilteredData] = useState([]);
  /** Full product-aggregated list when warehouse filter is All (client-paged). */
  const [aggregatedPositionRows, setAggregatedPositionRows] = useState([]);
  const [positionClientPaged, setPositionClientPaged] = useState(false);
  const [systemSummaries, setSystemSummaries] = useState([]);
  const [availableStockValueTotal, setAvailableStockValueTotal] = useState(null);
  const [positionTotal, setPositionTotal] = useState(0);
  const [positionTotalPages, setPositionTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    productId: "All",
    warehouseLocationId: "All",
    otherFilter: "All",
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const productFetchLock = useRef(false);
  const productLookupRef = useRef({ page: 0, hasMore: false, loaded: false });
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingMoreWarehouses, setLoadingMoreWarehouses] = useState(false);
  const warehouseFetchLock = useRef(false);
  const warehouseLookupRef = useRef({ page: 0, hasMore: false, loaded: false });

  // Journal tab dataset states
  const [filteredJournal, setFilteredJournal] = useState([]);
  const [journalTotalItems, setJournalTotalItems] = useState(0);
  const [journalFilters, setJournalFilters] = useState({
    productId: "All",
    warehouseLocationId: "All",
    movementType: "All",
    registerType: "All",
    startDate: "",
    endDate: "",
  });
  const [journalFilterModalOpen, setJournalFilterModalOpen] = useState(false);
  const [journalPage, setJournalPage] = useState(1);
  const JOURNAL_PAGE_SIZE = 10;

  const handleTabChange = useCallback(
    (tab) => {
      setActiveTab(tab);
      setError("");
      if (tab === "position") {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab }, { replace: true });
      }
    },
    [setSearchParams]
  );

  const loadProductOptions = useCallback(async (page, { append = false } = {}) => {
    if (productFetchLock.current) return;
    productFetchLock.current = true;
    if (append) setLoadingMoreProducts(true);
    else setLoadingProducts(true);

    try {
      const result = await inventoryManagementService.getInventoryProducts({
        page,
        size: FILTER_LOOKUP_PAGE_SIZE,
      });
      const next = toFilterOptions(result.content);
      setProductOptions((prev) => (append ? mergeFilterOptions(prev, next) : next));
      productLookupRef.current = {
        page: result.page ?? page,
        hasMore: Boolean(result.hasMore),
        loaded: true,
      };
    } catch (err) {
      console.error("Failed to load product filter lookups:", err);
      if (!append) {
        setProductOptions([]);
        productLookupRef.current = { page: 0, hasMore: false, loaded: false };
      }
    } finally {
      setLoadingProducts(false);
      setLoadingMoreProducts(false);
      productFetchLock.current = false;
    }
  }, []);

  const loadWarehouseOptions = useCallback(async (page, { append = false } = {}) => {
    if (warehouseFetchLock.current) return;
    warehouseFetchLock.current = true;
    if (append) setLoadingMoreWarehouses(true);
    else setLoadingWarehouses(true);

    try {
      const result = await inventoryManagementService.getWarehouses({
        page,
        size: FILTER_LOOKUP_PAGE_SIZE,
      });
      const next = toFilterOptions(result.content);
      setWarehouseOptions((prev) => (append ? mergeFilterOptions(prev, next) : next));
      warehouseLookupRef.current = {
        page: result.page ?? page,
        hasMore: Boolean(result.hasMore),
        loaded: true,
      };
    } catch (err) {
      console.error("Failed to load warehouse filter lookups:", err);
      if (!append) {
        setWarehouseOptions([]);
        warehouseLookupRef.current = { page: 0, hasMore: false, loaded: false };
      }
    } finally {
      setLoadingWarehouses(false);
      setLoadingMoreWarehouses(false);
      warehouseFetchLock.current = false;
    }
  }, []);

  const handleProductDropdownOpen = useCallback(
    (open) => {
      if (!open || productLookupRef.current.loaded || productFetchLock.current) return;
      loadProductOptions(0);
    },
    [loadProductOptions]
  );

  const handleWarehouseDropdownOpen = useCallback(
    (open) => {
      if (!open || warehouseLookupRef.current.loaded || warehouseFetchLock.current) return;
      loadWarehouseOptions(0);
    },
    [loadWarehouseOptions]
  );

  const handleProductListScroll = useCallback(
    (event) => {
      if (!productLookupRef.current.hasMore || productFetchLock.current) return;
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
        loadProductOptions(productLookupRef.current.page + 1, { append: true });
      }
    },
    [loadProductOptions]
  );

  const handleWarehouseListScroll = useCallback(
    (event) => {
      if (!warehouseLookupRef.current.hasMore || warehouseFetchLock.current) return;
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
        loadWarehouseOptions(warehouseLookupRef.current.page + 1, { append: true });
      }
    },
    [loadWarehouseOptions]
  );

  const warehouseFilterSelected =
    Boolean(filters.warehouseLocationId) && filters.warehouseLocationId !== "All";

  const resetPositionState = useCallback(() => {
    setFilteredData([]);
    setAggregatedPositionRows([]);
    setPositionClientPaged(false);
    setSystemSummaries([]);
    setAvailableStockValueTotal(null);
    setPositionTotal(0);
    setPositionTotalPages(1);
  }, []);

  // Warehouse selected → server-paged product rows for that warehouse.
  useEffect(() => {
    if (activeTab !== "position" || !warehouseFilterSelected) return;
    let active = true;
    setLoading(true);
    setError("");

    inventoryManagementService
      .getStockPositions({
        ...filters,
        page: page - 1,
        size: PAGE_SIZE,
        sortBy: "productName",
        sortDir: "ASC",
      })
      .then((res) => {
        if (!active) return;
        const rows = aggregateStockPositionsByProduct(res.content || []);
        setPositionClientPaged(false);
        setAggregatedPositionRows([]);
        setFilteredData(rows);
        setSystemSummaries(
          filters.otherFilter && filters.otherFilter !== "All"
            ? buildSystemSummariesFromRows(rows, filters.otherFilter)
            : res.systemSummaries || []
        );
        setAvailableStockValueTotal(
          res.availableStockValueTotal != null ? Number(res.availableStockValueTotal) : null
        );
        setPositionTotal(res.totalElements ?? rows.length);
        setPositionTotalPages(Math.max(1, res.totalPages ?? 1));
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter stock positions:", err);
        setError(t("validation.failedToRetrieveStockPositions"));
        resetPositionState();
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    activeTab,
    warehouseFilterSelected,
    filters,
    page,
    t,
    resetPositionState,
  ]);

  // Warehouse = All → fetch all product×warehouse rows, aggregate by product, client-page.
  useEffect(() => {
    if (activeTab !== "position" || warehouseFilterSelected) return;
    let active = true;
    setLoading(true);
    setError("");

    collectPageRecords(
      async (fetchPage, size) => {
        const otherFilterNorm = normalizeStockPositionOtherFilter(
          filters.otherFilter
        );
        // Dead stock uses server-side deadStock=DEAD; other "Other Filters" stay client-side
        const res = await inventoryManagementService.getStockPositions({
          productId: filters.productId,
          warehouseLocationId: "All",
          otherFilter:
            otherFilterNorm === STOCK_POSITION_OTHER_FILTER.DEAD_STOCK
              ? STOCK_POSITION_OTHER_FILTER.DEAD_STOCK
              : "All",
          page: fetchPage,
          size,
          sortBy: "productName",
          sortDir: "ASC",
        });
        return {
          content: res.content || [],
          totalElements: res.totalElements,
          totalPages: res.totalPages,
          pageNumber: fetchPage,
          pageSize: size,
          number: fetchPage,
          size,
          last: fetchPage >= Math.max(1, res.totalPages ?? 1) - 1,
          empty: !(res.content && res.content.length),
        };
      },
      POSITION_FETCH_PAGE_SIZE
    )
      .then((allRows) => {
        if (!active) return;
        const aggregated = aggregateStockPositionsByProduct(allRows);
        const filtered = applyStockPositionOtherFilter(aggregated, filters.otherFilter);
        const totalElements = filtered.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE) || 1);

        setPositionClientPaged(true);
        setAggregatedPositionRows(filtered);
        setFilteredData([]);
        setSystemSummaries(buildSystemSummariesFromRows(filtered, filters.otherFilter));
        const otherFilterNorm = normalizeStockPositionOtherFilter(filters.otherFilter);
        setAvailableStockValueTotal(
          filtered.reduce((sum, row) => {
            if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.DEFECTIVE) {
              return sum + (Number(row.defectiveValue) || 0);
            }
            if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY) {
              return (
                sum +
                (Number(row.serviceSafetyStockValue ?? row.maintenanceReserveValue) ||
                  0)
              );
            }
            if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY) {
              return sum + (Number(row.salesSafetyStockValue) || 0);
            }
            if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.RESERVED) {
              return sum + (Number(row.reservedStockValue) || 0);
            }
            return sum + (Number(row.availableStockValue) || 0);
          }, 0)
        );
        setPositionTotal(totalElements);
        setPositionTotalPages(totalPages);
        setPage((prev) => Math.min(prev, totalPages));
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter stock positions:", err);
        setError(t("validation.failedToRetrieveStockPositions"));
        resetPositionState();
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    activeTab,
    warehouseFilterSelected,
    filters.productId,
    filters.otherFilter,
    t,
    resetPositionState,
  ]);

  const positionTableData = useMemo(() => {
    if (!positionClientPaged) return filteredData;
    const start = (page - 1) * PAGE_SIZE;
    return aggregatedPositionRows.slice(start, start + PAGE_SIZE);
  }, [positionClientPaged, filteredData, aggregatedPositionRows, page]);

  const positionKpiRows = positionClientPaged ? aggregatedPositionRows : filteredData;

  // Fetch filtered Stock Journal
  useEffect(() => {
    if (activeTab !== "journal") return;
    let active = true;
    setLoading(true);
    setError("");

    const {
      registerType = "All",
      productId,
      warehouseLocationId,
      movementType,
      startDate,
      endDate,
    } = journalFilters;
    const apiFilters = {
      productId,
      warehouseLocationId,
      movementType,
      startDate,
      endDate,
    };
    const needsRegisterFilter = Boolean(registerType && registerType !== "All");

    const loadJournal = needsRegisterFilter
      ? collectPageRecords(
          (page, size) =>
            inventoryManagementService.getStockJournal({
              ...apiFilters,
              page,
              size,
            }),
          JOURNAL_REGISTER_FETCH_SIZE
        ).then((rows) => {
          const filtered = filterJournalByRegisterType(rows, registerType);
          const start = (journalPage - 1) * JOURNAL_PAGE_SIZE;
          return {
            content: filtered.slice(start, start + JOURNAL_PAGE_SIZE),
            totalElements: filtered.length,
          };
        })
      : inventoryManagementService.getStockJournal({
          ...apiFilters,
          page: journalPage - 1,
          size: JOURNAL_PAGE_SIZE,
        });

    loadJournal
      .then((res) => {
        if (!active) return;
        setFilteredJournal(res.content || []);
        setJournalTotalItems(res.totalElements ?? (res.content || []).length);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter stock journal:", err);
        setFilteredJournal([]);
        setJournalTotalItems(0);
        setError(t("validation.failedToRetrieveStockJournal"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [journalFilters, journalPage, activeTab, t]);

  // Compute active filters count (Position)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.productId !== "All") count++;
    if (filters.warehouseLocationId !== "All") count++;
    if (filters.otherFilter !== "All") count++;
    return count;
  }, [filters]);

  // Compute active filters count (Journal)
  const activeJournalFilterCount = useMemo(() => {
    let count = 0;
    if (journalFilters.productId !== "All") count++;
    if (journalFilters.warehouseLocationId !== "All") count++;
    if (journalFilters.movementType !== "All") count++;
    if (journalFilters.registerType && journalFilters.registerType !== "All") {
      count++;
    }
    if (journalFilters.startDate) count++;
    if (journalFilters.endDate) count++;
    return count;
  }, [journalFilters]);

  // Compute overall positions totals — KPI values/labels adapt to Other Filters
  const kpiStats = useMemo(() => {
    let stockValueSum = 0;
    let reservedSum = 0;
    let reservedValueSum = 0;
    let totalUnitsSum = 0;
    let defectiveValueSum = 0;
    let defectiveCountSum = 0;
    let serviceSafetyValueSum = 0;
    let serviceSafetyCountSum = 0;
    let salesSafetyValueSum = 0;
    let salesSafetyCountSum = 0;

    positionKpiRows.forEach((item) => {
      stockValueSum += Number(item.availableStockValue ?? item.stockValue) || 0;
      reservedSum += Number(item.reservedStock) || 0;
      reservedValueSum += Number(item.reservedStockValue) || 0;
      totalUnitsSum += Number(item.totalStock) || 0;
      defectiveValueSum += Number(item.defectiveValue) || 0;
      defectiveCountSum += Number(item.defectiveStock) || 0;
      serviceSafetyValueSum +=
        Number(item.serviceSafetyStockValue ?? item.maintenanceReserveValue) || 0;
      serviceSafetyCountSum +=
        Number(item.serviceSafetyStock ?? item.maintenanceReserve) || 0;
      salesSafetyValueSum += Number(item.salesSafetyStockValue) || 0;
      salesSafetyCountSum += Number(item.salesSafetyStock) || 0;
    });

    const otherFilterNorm = normalizeStockPositionOtherFilter(filters.otherFilter);

    const defaultStockValue =
      availableStockValueTotal != null && Number.isFinite(availableStockValueTotal)
        ? availableStockValueTotal
        : stockValueSum;

    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.DEFECTIVE) {
      return {
        stockValue: defectiveValueSum,
        reserved: defectiveCountSum,
        totalUnits: totalUnitsSum,
        stockValueLabelKey: "inventory.defectiveValue",
        reservedLabelKey: "inventory.defectiveUnits",
      };
    }

    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY) {
      return {
        stockValue: serviceSafetyValueSum,
        reserved: serviceSafetyCountSum,
        totalUnits: totalUnitsSum,
        stockValueLabelKey: "inventory.serviceSafetyStockValue",
        reservedLabelKey: "inventory.serviceSafetyStockUnits",
      };
    }

    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY) {
      return {
        stockValue: salesSafetyValueSum,
        reserved: salesSafetyCountSum,
        totalUnits: totalUnitsSum,
        stockValueLabelKey: "inventory.salesSafetyStockValue",
        reservedLabelKey: "inventory.salesSafetyStockUnits",
      };
    }

    if (otherFilterNorm === STOCK_POSITION_OTHER_FILTER.RESERVED) {
      return {
        stockValue: reservedValueSum,
        reserved: reservedSum,
        totalUnits: totalUnitsSum,
        stockValueLabelKey: "inventory.reservedStockValue",
        reservedLabelKey: "inventory.reservedUnits",
      };
    }

    return {
      stockValue: defaultStockValue,
      reserved: reservedSum,
      totalUnits: totalUnitsSum,
      stockValueLabelKey: "inventory.availableStockValue",
      reservedLabelKey: "inventory.reservedUnits",
    };
  }, [positionKpiRows, availableStockValueTotal, filters.otherFilter]);

  const handleApplyFilters = useCallback((nextFilters) => {
    setPage(1);
    setFilters({
      productId: nextFilters.productId || "All",
      warehouseLocationId: nextFilters.warehouseLocationId || "All",
      otherFilter: normalizeStockPositionOtherFilter(
        nextFilters.otherFilter || nextFilters.deadStock || "All"
      ),
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters({
      productId: "All",
      warehouseLocationId: "All",
      otherFilter: "All",
    });
  }, []);

  const handleApplyJournalFilters = useCallback((nextFilters) => {
    setJournalPage(1);
    setJournalFilters({
      productId: nextFilters.productId || "All",
      warehouseLocationId: nextFilters.warehouseLocationId || "All",
      movementType: nextFilters.movementType || "All",
      registerType: nextFilters.registerType || "All",
      startDate: nextFilters.startDate || "",
      endDate: nextFilters.endDate || "",
    });
  }, []);

  const handleResetJournalFilters = useCallback(() => {
    setJournalPage(1);
    setJournalFilters({
      productId: "All",
      warehouseLocationId: "All",
      movementType: "All",
      registerType: "All",
      startDate: "",
      endDate: "",
    });
  }, []);

  return (
    <AppLayout hideBreadcrumb className="space-y-6">
      <PageTitleRow back={<PageBackLink to="/" label={t("formActions.back")} />}>
        <h1 className={cn(ui.pageTitle, "uppercase")}>
          {t("inventory.inventoryManagement")}
        </h1>
      </PageTitleRow>

      {/* Tabs Switcher */}
      <InventoryTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Screen 1: Stock Position Tab */}
      {activeTab === "position" && (
        <div className="space-y-6">
          {/* KPI Dashboard Cards Grid (Compact Heights) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-sans">
            {/* Card 1: Stock Value (adapts to Other Filters) */}
            <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3 shadow-xs transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                  {t(kpiStats.stockValueLabelKey)}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                  {loading ? "..." : formatCurrency(kpiStats.stockValue)}
                </span>
              </div>
            </div>

            {/* Card 2: Reserved / Defective / Maint. Units */}
            <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3 shadow-xs transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                  {t(kpiStats.reservedLabelKey)}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                  {loading ? "..." : kpiStats.reserved.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Card 3: Total Units */}
            <div className="rounded-xl border border-(--color-text-border) bg-white px-4 py-3 shadow-xs transition-shadow hover:shadow-md flex flex-col justify-between">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider per-file-hint">
                  {t("inventory.totalUnits")}
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-foreground block mt-1 font-mono">
                  {loading ? "..." : kpiStats.totalUnits.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Table Container Card */}
          <ContentCard className="overflow-hidden">
            {/* Attached Header Panel */}
            <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
              <span className="text-xs font-bold text-foreground">{t("inventory.stockPositionsOverview")}</span>

              <div className="flex items-center gap-2">
                {/* Filter Modal Trigger */}
                <button
                  type="button"
                  onClick={() => setFilterModalOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-workspace/40 hover:text-primary transition cursor-pointer"
                >
                  <Funnel className="h-3.5 w-3.5" />
                  <span>{t("formActions.filter")}</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Reset filters */}
                <button
                  type="button"
                  onClick={handleResetFilters}
                  disabled={activeFilterCount === 0}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                >
                  <RefreshCw className="h-3.5 w-3.5 per-file-hint" />
                  <span>{t("formActions.resetFilters")}</span>
                </button>
              </div>
            </div>

            {loading ? (
              /* Custom beautiful Skeleton Loader */
              <div className="p-6 space-y-4 animate-pulse">
                <div className="h-10 bg-gray-100 rounded-lg w-full" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <div key={idx} className="h-14 bg-gray-50/50 rounded-lg w-full flex items-center justify-between px-4">
                      <div className="h-4 bg-gray-100 rounded w-1/4" />
                      <div className="h-4 bg-gray-100 rounded w-1/6" />
                      <div className="h-4 bg-gray-100 rounded w-12" />
                      <div className="h-4 bg-gray-100 rounded w-12" />
                      <div className="h-4 bg-gray-100 rounded w-16" />
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              /* Error Banner */
              <div className="p-8 text-center text-error font-semibold text-sm">
                ⚠️ {error}
              </div>
            ) : positionTotal === 0 ? (
              /* Empty state matching specifications */
              <div className="p-12 flex flex-col items-center text-center font-sans">
                <div className="h-12 w-12 rounded-full bg-workspace per-file-hint grid place-items-center mb-4">
                  <span>📦</span>
                </div>
                <h4 className="text-sm font-bold text-foreground">{t("inventory.noStockRecordsFound")}</h4>
                <p className="text-xs per-file-hint mt-1.5 leading-relaxed max-w-xs">
                  {t("inventory.noStockRecordsHint")}
                </p>
              </div>
            ) : (
              /* Stock Positions Table */
              <StockPositionTable
                data={positionTableData}
                systemSummaries={systemSummaries}
                page={page}
                pageSize={PAGE_SIZE}
                totalItems={positionTotal}
                totalPages={positionTotalPages}
                onPageChange={setPage}
                otherFilter={filters.otherFilter}
              />
            )}
          </ContentCard>
        </div>
      )}

      {/* Screen 2: Stock Journal Tab */}
      {activeTab === "journal" && (
        <div className="space-y-6">
          {/* Table Container Card */}
          <ContentCard className="overflow-hidden">
            {/* Attached Header Panel (Filters and Actions integrated) */}
            <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
              <span className="text-xs font-bold text-foreground">{t("inventory.stockJournalLogs")}</span>

              <div className="flex items-center gap-2">
                {/* Filter Modal Trigger */}
                <button
                  type="button"
                  onClick={() => setJournalFilterModalOpen(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-workspace/40 hover:text-primary transition cursor-pointer"
                >
                  <Funnel className="h-3.5 w-3.5" />
                  <span>{t("formActions.filter")}</span>
                  {activeJournalFilterCount > 0 && (
                    <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                      {activeJournalFilterCount}
                    </span>
                  )}
                </button>

                {/* Reset filters */}
                <button
                  type="button"
                  onClick={handleResetJournalFilters}
                  disabled={activeJournalFilterCount === 0}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                >
                  <RefreshCw className="h-3.5 w-3.5 per-file-hint" />
                  <span>{t("formActions.resetFilters")}</span>
                </button>
              </div>
            </div>

            {loading ? (
              /* Skeleton Loader */
              <div className="p-6 space-y-4 animate-pulse">
                <div className="h-10 bg-gray-100 rounded-lg w-full" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <div key={idx} className="h-12 bg-gray-50/50 rounded-lg w-full flex items-center gap-4 px-4">
                      <div className="h-4 bg-gray-100 rounded w-20" />
                      <div className="h-4 bg-gray-100 rounded w-24" />
                      <div className="h-4 bg-gray-100 rounded w-1/4" />
                      <div className="h-4 bg-gray-100 rounded w-12" />
                      <div className="h-4 bg-gray-100 rounded w-16" />
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              <div className="p-8 text-center text-error font-semibold text-sm">
                ⚠️ {error}
              </div>
            ) : filteredJournal.length === 0 ? (
              /* Empty state matching specifications */
              <div className="p-12 flex flex-col items-center text-center font-sans">
                <div className="h-12 w-12 rounded-full bg-workspace per-file-hint grid place-items-center mb-4">
                  <span>🗒️</span>
                </div>
                <h4 className="text-sm font-bold text-foreground">{t("inventory.noInventoryMovementsFound")}</h4>
                <p className="text-xs per-file-hint mt-1.5 leading-relaxed max-w-sm">
                  {t("inventory.noInventoryMovementsHint")}
                </p>
              </div>
            ) : (
              /* Stock Journal Audit Table */
              <InventoryJournalTable
                data={filteredJournal}
                page={journalPage}
                pageSize={JOURNAL_PAGE_SIZE}
                totalItems={journalTotalItems}
                onPageChange={setJournalPage}
              />
            )}
          </ContentCard>
        </div>
      )}

      {/* Stock Position Filters Modal */}
      <StockPositionFiltersModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        appliedFilters={filters}
        onApply={handleApplyFilters}
        productOptions={productOptions}
        warehouseOptions={warehouseOptions}
        loadingProducts={loadingProducts}
        loadingMoreProducts={loadingMoreProducts}
        loadingWarehouses={loadingWarehouses}
        loadingMoreWarehouses={loadingMoreWarehouses}
        onProductDropdownOpen={handleProductDropdownOpen}
        onWarehouseDropdownOpen={handleWarehouseDropdownOpen}
        onProductListScroll={handleProductListScroll}
        onWarehouseListScroll={handleWarehouseListScroll}
      />

      {/* Stock Journal Filters Modal */}
      <StockJournalFiltersModal
        open={journalFilterModalOpen}
        onOpenChange={setJournalFilterModalOpen}
        appliedFilters={journalFilters}
        onApply={handleApplyJournalFilters}
        productOptions={productOptions}
        warehouseOptions={warehouseOptions}
        loadingProducts={loadingProducts}
        loadingMoreProducts={loadingMoreProducts}
        loadingWarehouses={loadingWarehouses}
        loadingMoreWarehouses={loadingMoreWarehouses}
        onProductDropdownOpen={handleProductDropdownOpen}
        onWarehouseDropdownOpen={handleWarehouseDropdownOpen}
        onProductListScroll={handleProductListScroll}
        onWarehouseListScroll={handleWarehouseListScroll}
      />
    </AppLayout>
  );
}
